import { useMemo } from "react";
import { formatUsd } from "../aiUsage/format";

interface Props {
  calls: Array<{ startedAt: number; costMicroUsd: number | null }>;
  /** True when only the latest calls are loaded, so the title says so. */
  partial?: boolean;
  height?: number;
}

// What one call adds to the total: its cost, or nothing for an unpriced call.
function costOf(micro: number | null): number {
  return typeof micro === "number" && Number.isFinite(micro) && micro > 0 ? micro : 0;
}

// A session's running cost over its calls, oldest to newest: a hand-drawn SVG
// line (no chart library in the Security Room). It draws nothing for fewer
// than two calls, or when none of them cost anything.
export function CumulativeCostChart({ calls, partial = false, height = 120 }: Props) {
  const W = 720;
  const H = height;
  const gutterL = 48;
  const padR = 8;
  const padTop = 10;
  const gutterB = 18;
  const points = useMemo(() => {
    let sum = 0;
    return [...calls]
      .sort((a, b) => a.startedAt - b.startedAt)
      .map((c) => ({ at: c.startedAt, total: (sum += costOf(c.costMicroUsd)) }));
  }, [calls]);
  const total = points.length ? points[points.length - 1].total : 0;
  if (points.length < 2 || total <= 0) return null;
  const title = partial ? "Cost over the latest calls" : "Cost over time";
  const t0 = points[0].at;
  const span = Math.max(1, points[points.length - 1].at - t0);
  const x = (at: number) => gutterL + ((at - t0) / span) * (W - padR - gutterL);
  const y = (v: number) => H - gutterB - (v / total) * (H - gutterB - padTop);
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${x(p.at).toFixed(1)},${y(p.total).toFixed(1)}`)
    .join(" ");
  return (
    <div className="sr-chart">
      <div className="sr-chart__head">
        <div className="sr-chart__title">{title}</div>
      </div>
      <svg
        className="sr-chart__svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${title}, reaching ${formatUsd(total)}`}
      >
        <line
          x1={gutterL}
          x2={W - padR}
          y1={H - gutterB}
          y2={H - gutterB}
          stroke="var(--line, #26272c)"
        />
        <text
          x={gutterL - 6}
          y={padTop + 8}
          textAnchor="end"
          fill="var(--muted, #8a8e96)"
          fontSize="9"
        >
          {formatUsd(total)}
        </text>
        <path d={d} fill="none" stroke="var(--accent, #8ab4ff)" strokeWidth="2" />
      </svg>
    </div>
  );
}
